import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../../Context'
import { AuthContextApi } from '../../../../Context/api'
import { jsonConfig } from '../../../../Config'
import { toast } from 'react-toastify'
import {
    InformationCircleIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowsUpDownIcon,
    PlusIcon,
    DevicePhoneMobileIcon,
    ComputerDesktopIcon,
    ServerIcon,
    CpuChipIcon,
    WifiIcon,
    SignalIcon,
    Battery100Icon,
    XMarkIcon,
    CheckIcon,
    ChevronLeftIcon,
    PencilIcon,
    TrashIcon,
    DocumentDuplicateIcon,
    ClockIcon
} from '@heroicons/react/24/outline'
import { useParams, useNavigate } from 'react-router-dom'

const EquipamentoFormato = () => {

    const { states, setters } = useContext(AuthContext)
    const { getEquipamentosformatos, postEquipamentoFormato, putEquipamentoFormato, putEquipamentos, getEquipamentoFormatoHistorico } = useContext(AuthContextApi)
    const { id } = useParams()
    const navigate = useNavigate()

    // Estados locais para o formulário
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('nome')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(jsonConfig.limitDefault)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [selectedFormato, setSelectedFormato] = useState(null)
    const [formatoNome, setFormatoNome] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Estados para edição do equipamento
    const [isEquipamentoModalOpen, setIsEquipamentoModalOpen] = useState(false)
    const [equipamentoNome, setEquipamentoNome] = useState('')
    const [isEquipamentoSubmitting, setIsEquipamentoSubmitting] = useState(false)

    // Estados para dados do equipamento e formatos
    const [equipamento, setEquipamento] = useState(null)
    const [formatos, setFormatos] = useState([])
    const [isLoading, setIsLoading] = useState(true)

    // Estados para histórico de equipamento formato
    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false)
    const [historicoPage, setHistoricoPage] = useState(0)
    const [historicoHasMore, setHistoricoHasMore] = useState(true)
    const [historicoLoading, setHistoricoLoading] = useState(false)
    const [currentFormatoId, setCurrentFormatoId] = useState(null)

    // Opções para os campos de filtro (apenas para formatos)
    const filterOptions = [
        { value: 'data_atualizacao', label: 'Data de Atualização' },
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'nome', label: 'Nome' },
    ]

    // Opções para ordenação
    const sortOptions = [
        { value: 'data_atualizacao', label: 'Data de Atualização' },
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'nome', label: 'Nome' },
    ]

    // Opções para registros por página
    const recordsPerPageOptions = [
        { value: 10, label: '10 registros' },
        { value: 15, label: '15 registros' },
        { value: 25, label: '25 registros' },
        { value: 50, label: '50 registros' },
        { value: 100, label: '100 registros' }
    ]

    // Função para carregar dados
    const loadEquipamentoFormatos = async () => {
        if (!id) {
            console.log('loadEquipamentoFormatos - ID não encontrado')
            return
        }

        setIsLoading(true)
        try {
            const urlParams = new URLSearchParams()
            urlParams.set('page', states.page)
            urlParams.set('per_page', recordsPerPage)
            urlParams.set('sort', selectedSortField)
            urlParams.set('sort_dir', sortDirection)
            if (selectedFilterField && searchTerm) {
                urlParams.set('filter', selectedFilterField)
                urlParams.set('search', searchTerm)
            }

            const result = await getEquipamentosformatos(urlParams.toString(), id)

            // Os dados serão atualizados via context
        } catch (error) {
            console.error('Erro ao carregar dados:', error)
            toast.error('Erro ao carregar dados do equipamento')
        } finally {
            setIsLoading(false)
        }
    }

    // Função para realizar a busca
    const handleSearch = async () => {
        setters.setPage(0)
        await loadEquipamentoFormatos()
    }

    // Função para limpar o formulário
    const handleClear = async () => {
        setSearchTerm('')
        setSelectedFilterField('nome')
        setSelectedSortField('data_criacao')
        setSortDirection('desc')
        setRecordsPerPage(jsonConfig.limitDefault)
        setters.setPage(0)
        setters.setPer_page(jsonConfig.limitDefault)
        setters.setSort('data_criacao')
        setters.setSort_dir('desc')
        setters.setFilter(null)
        await loadEquipamentoFormatos()
    }

    // Função para lidar com Enter no campo de busca
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    // Função para navegação
    const handleNext = async () => {
        setters.setPage(states.page + 1)
        // Os dados serão recarregados automaticamente pelo useEffect
    }

    const handlePrevious = async () => {
        if (states.page > 0) {
            setters.setPage(states.page - 1)
            // Os dados serão recarregados automaticamente pelo useEffect
        }
    }

    // Função para abrir modal de cadastro/edição
    const openModal = (formato = null) => {

        setIsEditMode(!!formato)
        setSelectedFormato(formato)
        setFormatoNome(formato?.nome || '')
        setIsModalOpen(true)
    }

    // Função para fechar modal
    const closeModal = () => {
        setIsModalOpen(false)
        setSelectedFormato(null)
        setIsEditMode(false)
        setFormatoNome('')
        setIsSubmitting(false)
    }

    // Função para abrir modal de edição do equipamento
    const openEquipamentoModal = () => {
        if (equipamento) {
            setEquipamentoNome(equipamento.nome)
            setIsEquipamentoModalOpen(true)
        }
    }

    // Função para fechar modal do equipamento
    const closeEquipamentoModal = () => {
        setIsEquipamentoModalOpen(false)
        setEquipamentoNome('')
        setIsEquipamentoSubmitting(false)
    }

    // Função para salvar equipamento
    const handleSaveEquipamento = async () => {
        const nomeTrimmed = equipamentoNome.trim()

        if (!nomeTrimmed) {
            toast.error('O nome do equipamento é obrigatório')
            return
        }

        if (nomeTrimmed.length > 100) {
            toast.error('O nome do equipamento deve ter no máximo 100 caracteres')
            return
        }

        if (!equipamento?.id) {
            toast.error('ID do equipamento não encontrado')
            return
        }

        setIsEquipamentoSubmitting(true)

        try {
            const editData = {
                nome: nomeTrimmed,
                id: equipamento.id
            }

            const success = await putEquipamentos(editData)

            if (success) {
                closeEquipamentoModal()
                toast.success('Equipamento atualizado com sucesso')
                // Recarregar dados do equipamento
                setTimeout(() => {
                    loadEquipamentoFormatos()
                }, 500)
            }
        } catch (error) {
            toast.error('Erro ao salvar equipamento. Tente novamente.')
        } finally {
            setIsEquipamentoSubmitting(false)
        }
    }

    // Função para salvar formato
    const handleSaveFormato = async () => {
        const nomeTrimmed = formatoNome.trim()

        if (!nomeTrimmed) {
            toast.error('O nome do formato é obrigatório')
            return
        }

        if (nomeTrimmed.length > 100) {
            toast.error('O nome do formato deve ter no máximo 100 caracteres')
            return
        }

        if (isEditMode && !selectedFormato?.id) {
            toast.error('ID do formato não encontrado')
            return
        }

        setIsSubmitting(true)

        try {
            let success = false

            if (isEditMode) {
                const editData = {
                    nome: nomeTrimmed,
                    id: selectedFormato.id,
                    equipamento_id: id
                }
                success = await putEquipamentoFormato(editData)
            } else {
                const createData = {
                    nome: nomeTrimmed,
                    equipamento_id: id
                }
                success = await postEquipamentoFormato(createData)
            }

            if (success) {
                closeModal()
                // Recarregar dados após operação bem-sucedida
                setTimeout(() => {
                    console.log('Recarregando dados após operação bem-sucedida')
                    loadEquipamentoFormatos()
                }, 500)
            }
        } catch (error) {
            console.error('Erro ao salvar formato:', error)
            toast.error('Erro ao salvar formato. Tente novamente.')
        } finally {
            setIsSubmitting(false)
        }
    }



    // Função para formatar data
    const formatDate = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    // Função para abrir histórico de equipamento formato
    const openHistoricoFormato = async (formatoId) => {
        setCurrentFormatoId(formatoId)
        setHistoricoPage(0)
        setHistoricoHasMore(true)
        await loadHistoricoPage(formatoId, 0, true)
        setIsHistoricoOpen(true)
    }

    // Função para carregar página do histórico
    const loadHistoricoPage = async (formatoId, page, reset = false) => {
        if (!historicoLoading) {
            setHistoricoLoading(true)
            try {
                const query = new URLSearchParams({
                    page: page,
                    per_page: 10
                }).toString()

                await getEquipamentoFormatoHistorico(formatoId, query)

                // Verifica se há mais dados baseado no tamanho da resposta
                const currentData = states.equipamentoFormatoHistorico || []
                // setHistoricoHasMore(currentData.length === 10)
                setHistoricoPage(page)
            } catch (error) {
                console.error('Erro ao carregar histórico do formato:', error)
                toast.error('Erro ao carregar histórico do formato')
            } finally {
                setHistoricoLoading(false)
            }
        }
    }

    // Função para carregar mais histórico
    const loadMoreHistorico = () => {
        if (historicoHasMore && !historicoLoading && currentFormatoId) {
            loadHistoricoPage(currentFormatoId, historicoPage + 1, false)
        }
    }

    // Função para fechar modal de histórico
    const closeHistoricoModal = () => {
        setIsHistoricoOpen(false)
        setCurrentFormatoId(null)
        setHistoricoPage(0)
        setHistoricoHasMore(true)
        setHistoricoLoading(false)
    }

    // useEffect inicial para carregar dados quando a página for montada
    useEffect(() => {
        if (id) {
            console.log('useEffect inicial - Carregando dados para ID:', id)
            loadEquipamentoFormatos()
        }
    }, [id])

    // useEffect para monitorar mudanças nas variáveis de query
    useEffect(() => {
        if (id && !isLoading) {
            loadEquipamentoFormatos()
        }
    }, [states.page, recordsPerPage, selectedSortField, sortDirection])

    // useEffect para atualizar dados quando o context mudar
    useEffect(() => {
        if (states.dataEquipamentoFormato) {
            setEquipamento(states.dataEquipamentoFormato.equipamento)
            setFormatos(states.dataEquipamentoFormato.formatos || [])
        }
        if (states.dataEquipamentoFormato.length === 0) {
            loadEquipamentoFormatos()
        }
    }, [states.dataEquipamentoFormato])

    // useEffect para limpeza ao desmontar
    useEffect(() => {
        return () => {
            setters.setPage(0)
            setters.setPer_page(jsonConfig.limitDefault)
            setters.setSort("data_criacao")
            setters.setSort_dir("desc")
            setters.setFilter(null)
        }
    }, [])

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando dados do equipamento...</p>
                    <p className="text-sm text-gray-500 mt-2">ID: {id}</p>
                </div>
            </div>
        )
    }

    if (!id) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <XMarkIcon className="w-8 h-8 text-red-500" />
                    </div>
                    <p className="text-gray-600">ID do equipamento não encontrado</p>
                    <button
                        onClick={() => navigate('/cadastros/equipamento')}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Voltar aos Equipamentos
                    </button>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
                {/* Header */}
                <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center">
                                <button
                                    onClick={() => navigate('/cadastros/equipamento')}
                                    className="mr-4 p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
                                >
                                    <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
                                </button>
                                <div>
                                    <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                                        Formatos do Equipamento
                                    </h1>
                                    <p className="mt-2 text-sm text-gray-600">
                                        Gerencie os formatos do equipamento selecionado
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 sm:mt-0">
                                <button
                                    onClick={() => openModal()}
                                    className="inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                                >
                                    <PlusIcon className="w-5 h-5 mr-2" />
                                    Novo Formato
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Seção do Equipamento - Design Artístico */}
                    {equipamento && (
                        <div className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50/30 backdrop-blur-sm rounded-3xl shadow-lg border border-blue-100/50 p-8 mb-8 overflow-hidden">
                            {/* Background Pattern */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-3xl"></div>
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full -translate-y-16 translate-x-16"></div>
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-300/10 to-purple-300/10 rounded-full translate-y-12 -translate-x-12"></div>

                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="relative">
                                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 rounded-3xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-all duration-300">
                                            <CpuChipIcon className="w-10 h-10 text-white" />
                                        </div>
                                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
                                    </div>
                                    <div className="ml-6">
                                        <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                                            {equipamento.nome}
                                        </h2>
                                        <p className="text-sm text-gray-600 font-medium mt-1">
                                            ID: {equipamento.id?.slice(0, 8)}...
                                        </p>
                                        <div className="flex items-center mt-3 space-x-3">
                                            <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-700 border border-emerald-200 shadow-sm">
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></div>
                                                    Ativo
                                                </div>
                                            </span>
                                            <span className="text-sm text-gray-500 font-medium">
                                                Criado em {formatDate(equipamento.data_criacao)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => navigate('/cadastros/equipamento')}
                                        className="group relative inline-flex items-center px-5 py-3 border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 bg-white/80 hover:bg-white transition-all duration-300 shadow-sm hover:shadow-md"
                                    >
                                        <div className="relative z-10 flex items-center">
                                            <ChevronLeftIcon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                            <span>Voltar</span>
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </button>
                                    <button
                                        onClick={() => openEquipamentoModal()}
                                        className="group relative inline-flex items-center px-6 py-3 border border-blue-200 rounded-2xl text-sm font-medium text-blue-700 bg-gradient-to-r from-blue-50 to-blue-100/80 hover:from-blue-100 hover:to-blue-200/90 transition-all duration-300 shadow-sm hover:shadow-lg transform hover:scale-105"
                                    >
                                        <div className="relative z-10 flex items-center">
                                            <PencilIcon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                            <span className="font-semibold">Editar</span>
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-blue-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Formulário de Busca */}
                    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50 p-6 mb-8">
                        <div className="flex items-center mb-6">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-3">
                                <MagnifyingGlassIcon className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900">Pesquisa de Formatos</h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            {/* Campo de busca */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Termo de Busca
                                </label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Digite sua busca..."
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                                />
                            </div>

                            {/* Campo de filtro */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Campo para Filtrar
                                </label>
                                <div className="relative">
                                    <FunnelIcon className="absolute left-4 top-3.5 h-4 w-4 text-gray-400" />
                                    <select
                                        value={selectedFilterField}
                                        onChange={(e) => setSelectedFilterField(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-all duration-200"
                                    >
                                        {filterOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Campo de ordenação */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Ordenar por
                                </label>
                                <div className="relative">
                                    <ArrowsUpDownIcon className="absolute left-4 top-3.5 h-4 w-4 text-gray-400" />
                                    <select
                                        value={selectedSortField}
                                        onChange={(e) => setSelectedSortField(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-all duration-200"
                                    >
                                        {sortOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Direção da ordenação */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Direção
                                </label>
                                <select
                                    value={sortDirection}
                                    onChange={(e) => setSortDirection(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                                >
                                    <option value="asc">Crescente (A-Z)</option>
                                    <option value="desc">Decrescente (Z-A)</option>
                                </select>
                            </div>

                            {/* Registros por página */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Registros por Página
                                </label>
                                <select
                                    value={recordsPerPage}
                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                                >
                                    {recordsPerPageOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Botões de ação */}
                        <div className="flex flex-col sm:flex-row gap-3 mt-6">
                            <button
                                onClick={handleSearch}
                                className="flex-1 sm:flex-none inline-flex justify-center items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                            >
                                <MagnifyingGlassIcon className="w-4 h-4 mr-2" />
                                Pesquisar
                            </button>
                            <button
                                onClick={handleClear}
                                className="flex-1 sm:flex-none inline-flex justify-center items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                            >
                                Limpar Filtros
                            </button>
                        </div>
                    </div>

                    {/* Lista de Formatos - Design Artístico */}
                    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50 overflow-hidden">
                        {/* Cabeçalho da Tabela */}
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 px-6 py-4 border-b border-blue-200/50">
                            <div className="grid grid-cols-6 gap-4 text-sm font-semibold text-gray-700">
                                <div className="text-center">Ações</div>
                                <div className="text-center">Nome</div>
                                <div className="text-center">Criado em</div>
                                <div className="text-center">Atualizado em</div>
                                <div className="text-center">ID</div>
                                <div className="text-center">Status</div>
                            </div>
                        </div>

                        {/* Lista de Formatos */}
                        <div className="divide-y divide-gray-200/50">
                            {formatos.map((formato) => (
                                <div
                                    key={formato.id}
                                    className="px-6 py-4 hover:bg-blue-50/30 transition-colors duration-200 cursor-pointer group"
                                    onClick={() => openModal(formato)}
                                >
                                    <div className="grid grid-cols-6 gap-4 items-center">
                                        {/* Ações */}
                                        <div className="flex items-center justify-center space-x-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    openModal(formato)
                                                }}
                                                className="p-2 rounded-lg hover:bg-blue-100 transition-colors duration-200 group-hover:scale-110"
                                                title="Editar Formato"
                                            >
                                                <PencilIcon className="w-4 h-4 text-blue-600" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    openHistoricoFormato(formato.id)
                                                }}
                                                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 group-hover:scale-110"
                                                title="Ver Histórico"
                                            >
                                                <ClockIcon className="w-4 h-4 text-gray-600" />
                                            </button>
                                        </div>

                                        {/* Nome do formato */}
                                        <div className="text-center">
                                            <div className="flex items-center justify-center space-x-2">
                                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-sm">
                                                    <DocumentDuplicateIcon className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">
                                                    {formato.nome}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Data de criação */}
                                        <div className="text-center">
                                            <span className="text-sm text-gray-700">
                                                {formatDate(formato.data_criacao)}
                                            </span>
                                        </div>

                                        {/* Data de atualização */}
                                        <div className="text-center">
                                            <span className="text-sm text-gray-700">
                                                {formatDate(formato.data_atualizacao)}
                                            </span>
                                        </div>

                                        {/* ID */}
                                        <div className="text-center">
                                            <span className="text-sm text-gray-500 font-mono">
                                                {formato.id?.slice(0, 8)}...
                                            </span>
                                        </div>

                                        {/* Status */}
                                        <div className="text-center">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></div>
                                                Ativo
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Mensagem quando não há formatos */}
                        {formatos.length === 0 && (
                            <div className="text-center py-12">
                                <DocumentDuplicateIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum formato encontrado</h3>
                                <p className="text-gray-500">Clique em "Novo Formato" para criar o primeiro formato.</p>
                            </div>
                        )}
                    </div>

                    {/* Paginação */}
                    {formatos.length > 0 && (
                        <div className="mt-8 flex items-center justify-between">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={handlePrevious}
                                    disabled={states.page === 0}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                >
                                    Anterior
                                </button>
                                <button
                                    onClick={handleNext}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200"
                                >
                                    Próximo
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Mostrando <span className="font-medium">{states.page * recordsPerPage + 1}</span> a{' '}
                                        <span className="font-medium">
                                            {Math.min((states.page + 1) * recordsPerPage, formatos.length)}
                                        </span>{' '}
                                        de <span className="font-medium">{formatos.length}</span> resultados
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px">
                                        <button
                                            onClick={handlePrevious}
                                            disabled={states.page === 0}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-l-xl"
                                        >
                                            Anterior
                                        </button>
                                        <button
                                            onClick={handleNext}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-all duration-200 rounded-r-xl"
                                        >
                                            Próximo
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal de Cadastro/Edição */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-0 w-full max-w-md">
                            <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                                {/* Header do Modal */}
                                <div className="shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xl font-semibold text-white">
                                            {isEditMode ? 'Editar Formato' : 'Novo Formato'}
                                        </h3>
                                        <button
                                            onClick={closeModal}
                                            className="text-white/80 hover:text-white transition-colors"
                                        >
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>
                                </div>

                                {/* Conteúdo do Modal */}
                                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                    <div className="min-h-0 flex-1 overflow-y-auto p-6">
                                    <div className="space-y-6">
                                        {/* Ícone e Descrição */}
                                        <div className="text-center">
                                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                <DocumentDuplicateIcon className="w-8 h-8 text-white" />
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                {isEditMode
                                                    ? 'Atualize o nome do formato selecionado'
                                                    : 'Digite o nome do novo formato'
                                                }
                                            </p>
                                        </div>

                                        {/* Campo de Nome */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Nome do Formato
                                            </label>
                                            <input
                                                type="text"
                                                value={formatoNome}
                                                onChange={(e) => setFormatoNome(e.target.value)}
                                                placeholder="Digite o nome do formato..."
                                                maxLength={100}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !isSubmitting) {
                                                        handleSaveFormato()
                                                    }
                                                }}
                                            />
                                            <div className="flex justify-between text-xs text-gray-500">
                                                <span>Máximo 100 caracteres</span>
                                                <span>{formatoNome.length}/100</span>
                                            </div>
                                        </div>

                                        {/* Informações Adicionais */}
                                        <div className="bg-gray-50 rounded-xl p-4">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <InformationCircleIcon className="w-4 h-4 mr-2 text-blue-500" />
                                                <span>
                                                    {isEditMode
                                                        ? `Formato criado em ${formatDate(selectedFormato?.data_criacao)}`
                                                        : 'O formato será criado para este equipamento'
                                                    }
                                                </span>
                                            </div>
                                            {isEditMode && (
                                                <div className="mt-2 text-xs text-gray-500">
                                                    ID: {selectedFormato?.id}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    </div>

                                    {/* Botões de Ação */}
                                    <div className="flex shrink-0 gap-3 border-t border-gray-100 p-6 pt-4">
                                        <button
                                            onClick={closeModal}
                                            disabled={isSubmitting}
                                            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleSaveFormato}
                                            disabled={!formatoNome.trim() || isSubmitting}
                                            className="flex-1 px-4 py-3 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                    Salvando...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckIcon className="w-4 h-4 mr-2" />
                                                    {isEditMode ? 'Atualizar' : 'Criar'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Edição do Equipamento */}
                {isEquipamentoModalOpen && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-0 w-full max-w-md">
                            <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                                {/* Header do Modal */}
                                <div className="shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xl font-semibold text-white">
                                            Editar Equipamento
                                        </h3>
                                        <button
                                            onClick={closeEquipamentoModal}
                                            className="text-white/80 hover:text-white transition-colors"
                                        >
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>
                                </div>

                                {/* Conteúdo do Modal */}
                                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                    <div className="min-h-0 flex-1 overflow-y-auto p-6">
                                    <div className="space-y-6">
                                        {/* Ícone e Descrição */}
                                        <div className="text-center">
                                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                <CpuChipIcon className="w-8 h-8 text-white" />
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                Atualize o nome do equipamento selecionado
                                            </p>
                                        </div>

                                        {/* Campo de Nome */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Nome do Equipamento
                                            </label>
                                            <input
                                                type="text"
                                                value={equipamentoNome}
                                                onChange={(e) => setEquipamentoNome(e.target.value)}
                                                placeholder="Digite o nome do equipamento..."
                                                maxLength={100}
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !isEquipamentoSubmitting) {
                                                        handleSaveEquipamento()
                                                    }
                                                }}
                                            />
                                            <div className="flex justify-between text-xs text-gray-500">
                                                <span>Máximo 100 caracteres</span>
                                                <span>{equipamentoNome.length}/100</span>
                                            </div>
                                        </div>

                                        {/* Informações Adicionais */}
                                        <div className="bg-gray-50 rounded-xl p-4">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <InformationCircleIcon className="w-4 h-4 mr-2 text-blue-500" />
                                                <span>
                                                    Equipamento criado em {formatDate(equipamento?.data_criacao)}
                                                </span>
                                            </div>
                                            <div className="mt-2 text-xs text-gray-500">
                                                ID: {equipamento?.id}
                                            </div>
                                        </div>
                                    </div>
                                    </div>

                                    {/* Botões de Ação */}
                                    <div className="flex shrink-0 gap-3 border-t border-gray-100 p-6 pt-4">
                                        <button
                                            onClick={closeEquipamentoModal}
                                            disabled={isEquipamentoSubmitting}
                                            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleSaveEquipamento}
                                            disabled={!equipamentoNome.trim() || isEquipamentoSubmitting}
                                            className="flex-1 px-4 py-3 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                        >
                                            {isEquipamentoSubmitting ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                    Salvando...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckIcon className="w-4 h-4 mr-2" />
                                                    Atualizar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Histórico de Equipamento Formato */}
                {isHistoricoOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50">
                        <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                            {/* Header do Modal */}
                            <div className="shrink-0 border-b border-gray-100 px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                            <ClockIcon className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">
                                                Histórico do Formato
                                            </h2>
                                            <p className="text-sm text-gray-500">
                                                Visualize todas as alterações realizadas neste formato
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeHistoricoModal}
                                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors duration-200"
                                    >
                                        <XMarkIcon className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                            </div>

                            {/* Conteúdo do Histórico */}
                            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
                                {states.equipamentoFormatoHistorico && states.equipamentoFormatoHistorico.length > 0 ? (
                                    <div className="space-y-4">
                                        {states.equipamentoFormatoHistorico.map((item, index) => (
                                            <div key={item.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                                {/* Header do Item */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${item.transformacao === 'Criação' ? 'bg-green-100' :
                                                            item.transformacao === 'Edição' ? 'bg-blue-100' :
                                                                'bg-gray-100'
                                                            }`}>
                                                            {item.transformacao === 'Criação' ? (
                                                                <PlusIcon className="w-4 h-4 text-green-600" />
                                                            ) : item.transformacao === 'Edição' ? (
                                                                <PencilIcon className="w-4 h-4 text-blue-600" />
                                                            ) : (
                                                                <TrashIcon className="w-4 h-4 text-gray-600" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-900">
                                                                {item.transformacao}
                                                            </h3>
                                                            <p className="text-xs text-gray-500">
                                                                por {item.user_email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(item.data_atualizacao).toLocaleDateString('pt-BR')}
                                                        </p>
                                                        <p className="text-xs text-gray-400">
                                                            {new Date(item.data_atualizacao).toLocaleTimeString('pt-BR')}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Dados Atuais */}
                                                {item.dado_atual && (
                                                    <div className="mb-3">
                                                        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                                            Dados Atuais
                                                        </h4>
                                                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                {item.dado_atual.nome && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Nome</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{item.dado_atual.nome}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_atual.data_criacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Criado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_atual.data_criacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_atual.data_atualizacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Atualizado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_atual.data_atualizacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_atual.deletado !== undefined && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Status</p>
                                                                        <p className="text-sm font-semibold text-gray-900">
                                                                            {item.dado_atual.deletado ? 'Deletado' : 'Ativo'}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Dados Anteriores (se houver) */}
                                                {item.dado_antigo && (
                                                    <div>
                                                        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                                            Dados Anteriores
                                                        </h4>
                                                        <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                {item.dado_antigo.nome && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Nome</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{item.dado_antigo.nome}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_antigo.data_criacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Criado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_antigo.data_criacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_antigo.data_atualizacao && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Atualizado em</p>
                                                                        <p className="text-sm font-semibold text-gray-900">{formatDate(item.dado_antigo.data_atualizacao)}</p>
                                                                    </div>
                                                                )}
                                                                {item.dado_antigo.deletado !== undefined && (
                                                                    <div>
                                                                        <p className="text-xs font-medium text-gray-500">Status</p>
                                                                        <p className="text-sm font-semibold text-gray-900">
                                                                            {item.dado_antigo.deletado ? 'Deletado' : 'Ativo'}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* Botão Carregar Mais */}
                                        {historicoHasMore && (
                                            <div className="flex justify-center mt-6">
                                                <button
                                                    onClick={loadMoreHistorico}
                                                    disabled={historicoLoading}
                                                    className="group relative inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 via-blue-700 to-blue-700 hover:from-blue-700 hover:via-blue-800 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {historicoLoading ? (
                                                        <>
                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                            Carregando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <PlusIcon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                                            Carregar Mais
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum histórico encontrado</h3>
                                        <p className="text-gray-500">Este formato ainda não possui histórico de alterações.</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer do Modal */}
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                                <button
                                    onClick={closeHistoricoModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default EquipamentoFormato